// frontend/src/pages/Services/BulkRevokeModal.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Modal, Form, Select, Input, Space, Button, Tag,
  Typography, message, Table, Checkbox, Alert,
} from 'antd';
import { servicesAPI, employeesAPI } from '../../api/client';
import { LICENSE_TYPE_LABELS, LICENSE_TYPE_COLORS, formatDate } from './constants';

const { Text } = Typography;

export default function BulkRevokeModal({ open, onClose, onSuccess }) {
  const [form]           = Form.useForm();
  const [employeeId, setEmployeeId] = useState(null);
  const [selected, setSelected]     = useState([]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setEmployeeId(null);
      setSelected([]);
    }
  }, [open]);

  const { data: employeesData } = useQuery({
    queryKey: ['employees-active'],
    queryFn:  () => employeesAPI.getAll({ is_active: true, limit: 999 }).then(r => r.data),
    enabled:  !!open,
  });

  const { data: licenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['employee-licenses-revoke', employeeId],
    queryFn:  () => servicesAPI.getAssignments({ employee_id: employeeId, active_only: true, limit: 200 }).then(r => r.data),
    enabled:  !!employeeId,
  });

  const licenseItems = licenses?.items || [];

  const revokeMutation = useMutation({
    mutationFn: async ({ ids, notes }) => {
      const results = [];
      for (const id of ids) {
        try {
          await servicesAPI.revoke(id, { notes: notes || null });
          results.push({ ok: true });
        } catch (e) {
          results.push({ ok: false, error: e?.response?.data?.detail });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const ok     = results.filter(r => r.ok).length;
      const failed = results.filter(r => !r.ok).length;
      if (failed > 0) {
        message.warning(`Отозвано: ${ok}, ошибок: ${failed}`);
      } else {
        message.success(`Отозвано ${ok} доступов`);
      }
      onClose();
      onSuccess?.();
    },
  });

  const handleSubmit = async () => {
    if (!selected.length) return message.error('Выберите хотя бы один доступ');
    const vals = await form.validateFields();
    revokeMutation.mutate({ ids: selected, notes: vals.notes });
  };

  const columns = [
    {
      title: '', key: 'check', width: 40,
      render: (_, r) => (
        <Checkbox
          checked={selected.includes(r.id)}
          onChange={e => {
            if (e.target.checked) setSelected(p => [...p, r.id]);
            else setSelected(p => p.filter(id => id !== r.id));
          }}
        />
      ),
    },
    {
      title: 'Сервис', dataIndex: 'service_name', key: 'service', width: 160,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          <Tag color={r.category_color} style={{ fontSize: 10 }}>{r.category_name}</Tag>
        </Space>
      ),
    },
    {
      title: 'Тип доступа', dataIndex: 'license_type', key: 'license_type', width: 130,
      render: (v) => (
        <Tag color={LICENSE_TYPE_COLORS[v]} style={{ fontSize: 10, margin: 0 }}>
          {LICENSE_TYPE_LABELS[v] || v || '—'}
        </Tag>
      ),
    },
    {
      title: 'Выдан', dataIndex: 'assigned_at', key: 'date', width: 95,
      render: v => <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text>,
    },
  ];

  return (
    <Modal
      open={open}
      title={
        <Space>
          <Tag color="red" style={{ marginRight: 0 }}>Отзыв</Tag>
          <span>доступов</span>
        </Space>
      }
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Отозвать"
      okButtonProps={{ danger: true }}
      cancelText="Отмена"
      confirmLoading={revokeMutation.isPending}
      width={640} destroyOnClose
    >
      <Form form={form} layout="vertical">

        {/* Сотрудник */}
        <Form.Item name="employee_id" label={<Text strong>Сотрудник</Text>}
          rules={[{ required: true, message: 'Выберите сотрудника' }]}>
          <Select
            showSearch optionFilterProp="label"
            placeholder="Выберите сотрудника"
            onChange={v => { setEmployeeId(v); setSelected([]); }}
            options={(employeesData?.items || employeesData || []).map(e => ({
              label: `${e.full_name}${e.job_title ? ` — ${e.job_title}` : ''}`,
              value: e.id,
            }))}
          />
        </Form.Item>

        {/* Список доступов */}
        {employeeId && (
          <>
            {licenseItems.length > 0 && (
              <Space style={{ marginBottom: 8 }}>
                <a onClick={() => setSelected(licenseItems.map(r => r.id))}>Выбрать все</a>
                <a onClick={() => setSelected([])} style={{ color: '#ff4d4f' }}>Снять все</a>
                {selected.length > 0 && (
                  <Text type="secondary">Выбрано: {selected.length}</Text>
                )}
              </Space>
            )}
            {!licensesLoading && licenseItems.length === 0 && (
              <Alert type="info" message="У сотрудника нет активных доступов" showIcon style={{ marginBottom: 12 }} />
            )}
            {licenseItems.length > 0 && (
              <Table
                dataSource={licenseItems} columns={columns}
                rowKey="id" size="small" pagination={false}
                loading={licensesLoading}
                scroll={{ y: 240 }}
                style={{ marginBottom: 12 }}
              />
            )}
          </>
        )}

        <Form.Item name="notes" label="Комментарий">
          <Input.TextArea rows={2} placeholder="Необязательно" />
        </Form.Item>

      </Form>
    </Modal>
  );
}